import { useEffect, useRef, useState } from "react";
import { m, useAnimation, useInView } from "framer-motion";
import { ssr } from "@/lib/ssrState";

const HIDDEN = { opacity: 0, y: 60 };
const SHOWN = { opacity: 1, y: 0 };

/**
 * Scroll-reveal wrapper.
 * - Client-side navigation: original behaviour (fade/slide in when visible).
 * - Prerendered first load: content is visible in the HTML; after hydration
 *   only sections that are below the fold are armed for the reveal, so
 *   above-the-fold content is never hidden.
 */
const AnimatedSection = ({ children, delay = 0 }) => {
  const prerendered = useRef(ssr.prerender).current;

  if (!prerendered) {
    return (
      <m.section
        initial={HIDDEN}
        whileInView={SHOWN}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.7, delay }}
      >
        {children}
      </m.section>
    );
  }

  return <PrerenderedSection delay={delay}>{children}</PrerenderedSection>;
};

const PrerenderedSection = ({ children, delay }) => {
  const ref = useRef(null);
  const controls = useAnimation();
  const inView = useInView(ref, { once: true, amount: 0.2 });
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (el && el.getBoundingClientRect().top > window.innerHeight) {
      controls.set(HIDDEN);
      setArmed(true);
    }
  }, [controls]);

  useEffect(() => {
    if (armed && inView) {
      controls.start({ ...SHOWN, transition: { duration: 0.7, delay } });
    }
  }, [armed, inView, controls, delay]);

  return (
    <m.section ref={ref} initial={false} animate={controls}>
      {children}
    </m.section>
  );
};

export default AnimatedSection;
